import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { Logo } from '../components/Logo';
import { ApiError, apiFetch } from '../lib/api';

const SignupSchema = z.object({
  municipio: z.object({
    nome: z.string().min(1, 'obrigatório'),
    uf: z
      .string()
      .length(2, '2 letras')
      .regex(/^[A-Za-z]+$/, 'somente letras')
      .transform((s) => s.toUpperCase()),
    cnpj: z.string().min(14, 'cnpj com 14 dígitos'),
    codigo_ibge: z
      .string()
      .optional()
      .transform((s) => (s === '' ? undefined : s)),
    populacao: z.coerce.number().int().positive().optional(),
  }),
  agente: z.object({
    nome_completo: z.string().min(1, 'obrigatório'),
    email: z.string().email('email inválido').toLowerCase(),
    cpf: z.string().min(11, 'cpf com 11 dígitos'),
    matricula: z.string().min(1, 'obrigatório'),
    cargo: z.string().min(1, 'obrigatório'),
  }),
});

type FormState = Record<string, string>;

const onlyDigits = (s: string) => s.replace(/\D/g, '');

interface SignupResponse {
  readonly municipio_id: string;
  readonly agente_id: string;
  readonly email: string;
  readonly message: string;
}

export const SignupPage = () => {
  const nav = useNavigate();
  const [errors, setErrors] = useState<FormState>({});

  const mutation = useMutation({
    mutationFn: async (data: z.infer<typeof SignupSchema>) =>
      apiFetch<SignupResponse>('/v1/auth/signup', { method: 'POST', body: data }),
    onSuccess: (r) => {
      nav('/verify', { state: { email: r.email } });
    },
  });

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrors({});
    const fd = new FormData(e.currentTarget);
    const raw = {
      municipio: {
        nome: String(fd.get('mun_nome') ?? ''),
        uf: String(fd.get('mun_uf') ?? ''),
        cnpj: onlyDigits(String(fd.get('mun_cnpj') ?? '')),
        codigo_ibge: String(fd.get('mun_ibge') ?? ''),
        populacao: fd.get('mun_pop') ? String(fd.get('mun_pop')) : undefined,
      },
      agente: {
        nome_completo: String(fd.get('ag_nome') ?? ''),
        email: String(fd.get('ag_email') ?? ''),
        cpf: onlyDigits(String(fd.get('ag_cpf') ?? '')),
        matricula: String(fd.get('ag_matricula') ?? ''),
        cargo: String(fd.get('ag_cargo') ?? ''),
      },
    };
    const parsed = SignupSchema.safeParse(raw);
    if (!parsed.success) {
      const flat: FormState = {};
      for (const issue of parsed.error.issues) {
        flat[issue.path.join('.')] = issue.message;
      }
      setErrors(flat);
      return;
    }
    mutation.mutate(parsed.data);
  };

  const err = (field: string): string | undefined => errors[field];
  const apiErr = mutation.error instanceof ApiError ? mutation.error : null;

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-2xl">
        <Logo className="text-center mb-6" />
        <form onSubmit={onSubmit} className="card space-y-6">
          <div>
            <h2 className="text-xl font-semibold mb-1">criar conta</h2>
            <p className="text-sm text-navy-500">
              cadastro do município + primeiro agente responsável.
            </p>
          </div>

          {apiErr && (
            <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-700">
              <strong className="font-medium">{apiErr.code}:</strong> {apiErr.message}
            </div>
          )}

          <fieldset className="space-y-3">
            <legend className="font-display text-sm font-semibold uppercase tracking-wider text-gold-600">
              município
            </legend>

            <div>
              <label className="label" htmlFor="mun_nome">
                nome
              </label>
              <input
                id="mun_nome"
                name="mun_nome"
                className="input"
                required
                placeholder="ex: Uberaba"
              />
              {err('municipio.nome') && <p className="error">{err('municipio.nome')}</p>}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label" htmlFor="mun_uf">
                  UF
                </label>
                <input
                  id="mun_uf"
                  name="mun_uf"
                  className="input uppercase"
                  maxLength={2}
                  required
                  placeholder="MG"
                />
                {err('municipio.uf') && <p className="error">{err('municipio.uf')}</p>}
              </div>
              <div>
                <label className="label" htmlFor="mun_ibge">
                  código IBGE (opcional)
                </label>
                <input
                  id="mun_ibge"
                  name="mun_ibge"
                  className="input"
                  pattern="[0-9]{7}"
                  placeholder="3170107"
                />
              </div>
            </div>

            <div>
              <label className="label" htmlFor="mun_cnpj">
                CNPJ
              </label>
              <input
                id="mun_cnpj"
                name="mun_cnpj"
                className="input font-mono"
                required
                placeholder="00.000.000/0000-00"
              />
              {err('municipio.cnpj') && <p className="error">{err('municipio.cnpj')}</p>}
              <p className="hint">com ou sem máscara — validamos os dígitos verificadores.</p>
            </div>
          </fieldset>

          <fieldset className="space-y-3">
            <legend className="font-display text-sm font-semibold uppercase tracking-wider text-gold-600">
              agente responsável
            </legend>

            <div>
              <label className="label" htmlFor="ag_nome">
                nome completo
              </label>
              <input id="ag_nome" name="ag_nome" className="input" required />
              {err('agente.nome_completo') && (
                <p className="error">{err('agente.nome_completo')}</p>
              )}
            </div>

            <div>
              <label className="label" htmlFor="ag_email">
                email institucional
              </label>
              <input
                id="ag_email"
                name="ag_email"
                type="email"
                className="input"
                required
                placeholder="agente@prefeitura.gov.br"
              />
              {err('agente.email') && <p className="error">{err('agente.email')}</p>}
              <p className="hint">você vai receber um link mágico aqui pra entrar.</p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label" htmlFor="ag_cpf">
                  CPF
                </label>
                <input
                  id="ag_cpf"
                  name="ag_cpf"
                  className="input font-mono"
                  required
                  placeholder="000.000.000-00"
                />
                {err('agente.cpf') && <p className="error">{err('agente.cpf')}</p>}
              </div>
              <div>
                <label className="label" htmlFor="ag_matricula">
                  matrícula
                </label>
                <input id="ag_matricula" name="ag_matricula" className="input" required />
              </div>
            </div>

            <div>
              <label className="label" htmlFor="ag_cargo">
                cargo
              </label>
              <input
                id="ag_cargo"
                name="ag_cargo"
                className="input"
                required
                placeholder="Pregoeiro / Agente de Contratação"
              />
            </div>
          </fieldset>

          <div className="flex items-center justify-between pt-2">
            <Link to="/login" className="text-sm text-navy-600 hover:text-navy-800 underline">
              já tem conta?
            </Link>
            <button type="submit" className="btn-primary" disabled={mutation.isPending}>
              {mutation.isPending ? 'criando...' : 'criar conta'}
            </button>
          </div>
        </form>
        <p className="text-center text-xs text-navy-500 mt-6">by lions intelligence</p>
      </div>
    </div>
  );
};
